import { z } from 'zod';

export const IdpSchema = z.enum(['microsoft', 'google', 'dev']);
export type Idp = z.infer<typeof IdpSchema>;

export const OrgRoleSchema = z.enum(['admin', 'contributor', 'reviewer']);
export type OrgRole = z.infer<typeof OrgRoleSchema>;

/** Effective role in an org context (member roles + assessor + platform admin). */
export const OrgContextRoleSchema = z.enum([
  'admin',
  'contributor',
  'reviewer',
  'assessor',
  'system_admin',
]);
export type OrgContextRole = z.infer<typeof OrgContextRoleSchema>;

export const StandardStatusSchema = z.enum(['draft', 'active', 'deprecated', 'archived']);
export type StandardStatus = z.infer<typeof StandardStatusSchema>;

export const ResponseTypeSchema = z.enum([
  'markdown',
  'yes_no',
  'multiple_choice',
  'file_upload',
  'table',
  'date',
  'number',
]);
export type ResponseType = z.infer<typeof ResponseTypeSchema>;

export const ResponseStatusSchema = z.enum([
  'not_started',
  'in_progress',
  'drafted',
  'under_review',
  'changes_requested',
  'finalised',
  'assessed',
  'locked',
]);
export type ResponseStatus = z.infer<typeof ResponseStatusSchema>;

export const ReviewStateSchema = z.enum(['none', 'pending', 'approved', 'changes_requested']);
export type ReviewState = z.infer<typeof ReviewStateSchema>;

export const ResponseTransitionSchema = z.enum([
  'mark_drafted',
  'submit_for_review',
  'approve',
  'request_changes',
  'finalise',
  'mark_assessed',
  'reopen',
]);
export type ResponseTransition = z.infer<typeof ResponseTransitionSchema>;

export const AssessmentStatusSchema = z.enum(['open', 'locked', 'archived']);
export type AssessmentStatus = z.infer<typeof AssessmentStatusSchema>;

export const CommentKindSchema = z.enum(['reviewer', 'assessor', 'reply']);
export type CommentKind = z.infer<typeof CommentKindSchema>;

export const CommentStatusSchema = z.enum(['open', 'resolved', 'rejected']);
export type CommentStatus = z.infer<typeof CommentStatusSchema>;

export const ConfidenceSchema = z.enum(['high', 'medium', 'low']);
export type Confidence = z.infer<typeof ConfidenceSchema>;

export const RetentionStatusSchema = z.enum(['active', 'superseded', 'deleted', 'retained']);
export type RetentionStatus = z.infer<typeof RetentionStatusSchema>;

export const ChangeTypeSchema = z.enum(['new', 'changed', 'removed', 'unchanged']);
export type ChangeType = z.infer<typeof ChangeTypeSchema>;

export const CorpusStatusSchema = z.enum(['draft', 'approved', 'archived']);
export type CorpusStatus = z.infer<typeof CorpusStatusSchema>;

export const JobStatusSchema = z.enum(['pending', 'running', 'succeeded', 'failed']);
export type JobStatus = z.infer<typeof JobStatusSchema>;

export const AiKindSchema = z.enum([
  'draft',
  'improve',
  'gap_check',
  'evidence_suggest',
  'summarise',
  'map_corpus',
]);
export type AiKind = z.infer<typeof AiKindSchema>;

export const AiDispositionSchema = z.enum(['accepted', 'rejected', 'edited']);
export type AiDisposition = z.infer<typeof AiDispositionSchema>;

export const ExportFormatSchema = z.enum(['markdown', 'html', 'pdf', 'docx', 'csv', 'json', 'zip']);
export type ExportFormat = z.infer<typeof ExportFormatSchema>;

export const PlanSchema = z.enum(['free', 'paid']);
export type Plan = z.infer<typeof PlanSchema>;

export const SubscriptionStatusSchema = z.enum([
  'none',
  'active',
  'past_due',
  'canceled',
  'trialing',
]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const TokenScopeSchema = z.enum(['read', 'write', 'admin']);
export type TokenScope = z.infer<typeof TokenScopeSchema>;

export const WebhookEventSchema = z.enum([
  'export.completed',
  'response.status_changed',
  'comment.created',
]);
export type WebhookEvent = z.infer<typeof WebhookEventSchema>;

/** Content format of a stored response version. */
export const ContentFormatSchema = z.enum(['markdown', 'json']);
export type ContentFormat = z.infer<typeof ContentFormatSchema>;
